import React from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Checkbox,
  Chip,
  Box,
  Badge,
} from "@mui/material";
import { IconEdit, IconTrash, IconPrinter, IconHistory, IconCalculator, IconScale, IconDownload, IconRestore } from "@tabler/icons-react";
import { EditButton, DeleteButton, PrintButton, FileButton } from "components/ui/buttons/StyledButtons";
import { ButtonBase } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import moment from "moment";
import { formatRateAbusivenessMessage } from "utils/rate-abusiveness";
import CustomerNameButton from "components/customer360/CustomerNameButton";

const RestoreButton = styled(ButtonBase)(() => ({
  color: "#00451A",
  "&:hover": {
    background: "#00451A",
    color: "#b9f6ca",
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const ContractsTable = ({ 
  contracts, 
  onEdit, 
  onDelete,
  onRestore,
  onPrint, 
  onPrintInitialCalculation, 
  onViewHistory, 
  onFile,
  onDownloadPack,
  formatDocument,
  selectionEnabled = false,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,
  showDeletedMode = false,
  permissions = {
    can_create_contracts: true,
    can_edit_contracts: true,
    can_delete_contracts: true,
    can_file_contracts: true,
    can_restore_contracts: false,
  }
}) => {
  const theme = useTheme();
  const [highlightedId, setHighlightedId] = React.useState(null);
  const selectedSet = React.useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const allOnPageSelected = selectionEnabled && contracts.length > 0 && contracts.every((c) => selectedSet.has(c.id));
  const someOnPageSelected = selectionEnabled && contracts.some((c) => selectedSet.has(c.id)) && !allOnPageSelected;

  const stickyCellBg = theme.palette.background.paper;
  const rowSelectedBg = theme.palette.secondary.light;
  const rowHoverBg = `color-mix(in srgb, ${theme.palette.secondary.main} 12%, ${stickyCellBg})`;

  const formatUserName = (user) => {
    if (!user) return "";
    if (user.full_name) return user.full_name;
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.username || "";
  };

  const handleRowClick = (event, contractId) => {
    if (event.target.closest("button, input, a, [role='button']")) return;
    setHighlightedId((prev) => (prev === contractId ? null : contractId));
  };

  return (
    <Table sx={{ minWidth: 1800 }}>
      <TableHead>
        <TableRow>
          {selectionEnabled && (
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someOnPageSelected}
                checked={allOnPageSelected}
                onChange={(e) => onToggleSelectAll && onToggleSelectAll(e.target.checked)}
                inputProps={{ "aria-label": "Selecionar todos os contratos desta página" }}
              />
            </TableCell>
          )}
          <TableCell>Número contrato</TableCell>
          <TableCell>Nome do cliente</TableCell>
          <TableCell>CPF</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Banco</TableCell>
          <TableCell>Situação</TableCell>
          <TableCell>Data Fechamento</TableCell>
          <TableCell>Tipo</TableCell>
          <TableCell>Nº Processo</TableCell>
          <TableCell>Nº Protocolo de Reclamação</TableCell>
          <TableCell>Ajuizamento</TableCell>
          <TableCell>Advogado da inicial</TableCell>
          <TableCell>Observação do atendimento</TableCell>
          <TableCell>Observação protocolo do processo</TableCell>
          <TableCell>UF</TableCell>
          <TableCell>Consultor de vendas</TableCell>
          <TableCell>Carteira</TableCell>
          <TableCell>Ajuizar em</TableCell>
          {permissions.can_file_contracts && (
            <TableCell>Responsável</TableCell>
          )}
          {showDeletedMode && (
            <>
              <TableCell>Excluído em</TableCell>
              <TableCell>Excluído por</TableCell>
            </>
          )}
          <TableCell align="right" sx={{ position: "sticky", right: 0, backgroundColor: stickyCellBg, zIndex: 1 }}>Ações</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {contracts.map((contract) => {
          const isHighlighted = highlightedId === contract.id;
          return (
          <TableRow
            key={contract.id}
            onClick={(e) => handleRowClick(e, contract.id)}
            sx={{
              cursor: "pointer",
              backgroundColor: isHighlighted ? rowSelectedBg : undefined,
              "&:hover": {
                backgroundColor: isHighlighted ? rowSelectedBg : rowHoverBg,
              },
              "&:hover .contracts-table-actions-cell": {
                backgroundColor: isHighlighted ? rowSelectedBg : rowHoverBg,
              },
            }}
          >
            {selectionEnabled && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedSet.has(contract.id)}
                  onChange={() => onToggleSelect && onToggleSelect(contract.id)}
                  inputProps={{ "aria-label": `Selecionar contrato ${contract.number || contract.id}` }}
                />
              </TableCell>
            )}
            <TableCell>{contract.number}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <CustomerNameButton customerId={contract.customer?.id}>{contract.customer?.name}</CustomerNameButton>
              {contract.customer?.id != null && (
                <span style={{ color: "#888", fontSize: "0.8em" }}> ({contract.customer.id})</span>
              )}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDocument(contract.customer.cpf)}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {(() => {
                const status = contract.customer?.documents_update_status;
                const updatedAt = contract.customer?.documents_last_updated_at;
                const dateLabel = updatedAt && moment(updatedAt).isValid()
                  ? moment(updatedAt).format("DD/MM/YYYY")
                  : "Sem data";

                if (status === "ATUALIZADO") {
                  return (
                    <Tooltip title={`Última atualização: ${dateLabel}`}>
                      <Chip
                        label="ATUALIZADO"
                        size="small"
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                    </Tooltip>
                  );
                }

                return (
                  <Tooltip title={`Última atualização: ${dateLabel}`}>
                    <Chip
                      label="DESATUALIZADO"
                      size="small"
                      sx={{
                        bgcolor: "#9a3412",
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    />
                  </Tooltip>
                );
              })()}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{contract.bank.name}</TableCell>
            <TableCell>
              {(() => {
                const statusMap = {
                  'AJUIZAR': 'AJUIZAR',
                  'NAO_POSSUI_ABUSIVIDADE': 'NÃO POSSUI ABUSIVIDADE',
                  'FALTAM_DOCUMENTOS': 'FALTAM DOCUMENTOS',
                  'CANCELADO': 'CANCELADO',
                  'PROTOCOLO_URGENTE': 'PROTOCOLO URGENTE',
                  'PROTOCOLADO': 'PROTOCOLADO',
                  'TRANSITO_EM_JULGADO': 'TRÂNSITO EM JULGADO',
                  'REAJUIZAR': 'REAJUIZAR',
                  'PASTA_FEITA': 'PASTA FEITA',
                  'DESATUALIZADO': 'DESATUALIZADO',
                };
                const statusLabel = statusMap[contract.status] || contract.status;
                if (!contract.is_rate_below_abusive_threshold) {
                  return statusLabel;
                }
                return (
                  <Tooltip
                    title={formatRateAbusivenessMessage(
                      null,
                      null,
                      contract.rate_excess_percent
                    )}
                  >
                    <Badge
                      color="warning"
                      badgeContent="!"
                      overlap="rectangular"
                      anchorOrigin={{ vertical: "top", horizontal: "right" }}
                      sx={{
                        "& .MuiBadge-badge": {
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          minWidth: 16,
                          height: 16,
                          p: 0,
                          right: -6,
                          top: -4,
                        },
                      }}
                    >
                      <Box component="span" sx={{ display: "inline-block", pr: 1.25 }}>
                        {statusLabel}
                      </Box>
                    </Badge>
                  </Tooltip>
                );
              })()}
            </TableCell>
            <TableCell>
              {moment(contract.date).isValid() ? moment(contract.date).format("DD/MM/YYYY") : ""}
            </TableCell>
            <TableCell>
              {contract.category}
            </TableCell>
            <TableCell>{contract.process_number}</TableCell>
            <TableCell>{contract.complaint_protocol_number}</TableCell>
            <TableCell>{moment(contract.filed_at).isValid() ? moment(contract.filed_at).format("DD/MM/YYYY") : ""}</TableCell>
            <TableCell>{contract.lawyer?.name || ""}</TableCell>
            <TableCell>{contract.service_obs}</TableCell>
            <TableCell>{contract.process_protocol_obs}</TableCell>
            <TableCell>{contract.state}</TableCell>
            <TableCell>{contract.sales_consultant?.name || ""}</TableCell>
            <TableCell>{contract.lawyer_wallet?.name || ""}</TableCell>
            <TableCell>{moment(contract.file_at).isValid() ? moment(contract.file_at).format("DD/MM/YYYY") : ""}</TableCell>
            {permissions.can_file_contracts && (
              <TableCell>
                {formatUserName(contract.responsible_user)}
              </TableCell>
            )}
            {showDeletedMode && (
              <>
                <TableCell>
                  {contract.deleted_at && moment(contract.deleted_at).isValid()
                    ? moment(contract.deleted_at).format("DD/MM/YYYY HH:mm")
                    : ""}
                </TableCell>
                <TableCell>{formatUserName(contract.deleted_by)}</TableCell>
              </>
            )}
            <TableCell
              className="contracts-table-actions-cell"
              align="right"
              sx={{ position: "sticky", right: 0, zIndex: 1, backgroundColor: isHighlighted ? rowSelectedBg : stickyCellBg, minWidth: showDeletedMode ? 80 : 320 }}
            >
              {!showDeletedMode && (
                <>
                  <Tooltip title="Download do pack">
                    <PrintButton onClick={() => onDownloadPack && onDownloadPack(contract)}>
                      <IconDownload stroke={1.5} size="1.3rem" />
                    </PrintButton>
                  </Tooltip>
                  <Tooltip title="Imprimir repetição do indébito">
                    <PrintButton onClick={() => onPrint(contract.id)}>
                      <IconPrinter stroke={1.5} size="1.3rem" />
                    </PrintButton>
                  </Tooltip>
                  <Tooltip title="Imprimir cálculo da inicial">
                    <PrintButton onClick={() => onPrintInitialCalculation(contract.id)}>
                      <IconCalculator stroke={1.5} size="1.3rem" />
                    </PrintButton>
                  </Tooltip>
                  <Tooltip title="Ver Histórico">
                    <EditButton onClick={() => onViewHistory(contract)}>
                      <IconHistory stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  {permissions.can_edit_contracts && (
                    <Tooltip title="Editar">
                      <EditButton onClick={() => onEdit(contract)}>
                        <IconEdit stroke={1.5} size="1.3rem" />
                      </EditButton>
                    </Tooltip>
                  )}
                  {!permissions.can_edit_contracts && permissions.can_file_contracts && (
                    <Tooltip title="Ajuizar">
                      <FileButton onClick={() => onFile(contract)}>
                        <IconScale stroke={1.5} size="1.3rem" />
                      </FileButton>
                    </Tooltip>
                  )}
                </>
              )}
              {showDeletedMode && permissions.can_restore_contracts && (
                <Tooltip title="Restaurar contrato">
                  <RestoreButton onClick={() => onRestore && onRestore(contract.id)}>
                    <IconRestore stroke={1.5} size="1.3rem" />
                  </RestoreButton>
                </Tooltip>
              )}
              {!showDeletedMode && permissions.can_delete_contracts && (
                <Tooltip title="Excluir">
                  <DeleteButton onClick={() => onDelete(contract.id)}>
                    <IconTrash stroke={1.5} size="1.3rem" />
                  </DeleteButton>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ContractsTable; 